import type {Repository} from '@github-ui/current-repository'
import type {Icon} from '@primer/octicons-react'
import {CodespacesIcon, CopilotIcon, PeopleIcon, PersonAddIcon} from '@primer/octicons-react'
import {LinkButton, Stack} from '@primer/react'
import {Card} from '@primer/react/experimental'

import styles from './OnboardingPrompts.module.css'

export interface OnboardingPromptsProps {
  repo: Repository
}

/**
 * Top-of-page two-up grid shown to repo admins on an empty repo.
 *
 * TODO: All variant selection below is stubbed. The real decisions depend on
 * server-side state that isn't on `Repository` today:
 *   - Copilot: viewer's `Copilot::User` (access_allowed?, has_free_access?,
 *     can_signup_for_free?), whether the repo is in an org, whether the
 *     viewer is an org admin, RAF eligibility, enterprise/business gating.
 *   - Codespaces: `current_user_can_push?` plus the
 *     `codespaces_empty_repo_init` feature flag for the CTA href.
 *   - Secondary: `current_repository.in_organization?`,
 *     `current_repository.members_count`, single-/multi-tenant enterprise,
 *     EMU users.
 * Wire these in via a dedicated query/loader before shipping.
 */
export function OnboardingPrompts({repo}: OnboardingPromptsProps) {
  if (!repo.currentUserCanPush) return null

  const entitlements = deriveEntitlements(repo)

  return (
    <Stack
      direction={{narrow: 'vertical', regular: 'horizontal'}}
      gap="normal"
      align="stretch"
      className={styles.PromptsGrid}
    >
      <CodespacesOrCopilotPrompt entitlements={entitlements} />
      <InvitePrompt entitlements={entitlements} />
    </Stack>
  )
}

interface Entitlements {
  isOrgOwned: boolean
  isOrgAdmin: boolean
  isEnterpriseOrg: boolean
  showOrgAdminPrompt: boolean
  isEligibleForCopilot: boolean
  hasCopilotAccess: boolean
}

function deriveEntitlements(repo: Repository): Entitlements {
  // TODO: most of these depend on server-side state not yet on `Repository`.
  // Wire these in via a dedicated query/loader before shipping.
  return {
    isOrgOwned: repo.isOrgOwned,
    isOrgAdmin: false, // TODO: isOrgOwned && viewerCanAdminister
    isEnterpriseOrg: false, // TODO: repo.organization?.business
    showOrgAdminPrompt: false, // TODO: (isEnterprise || isEnterpriseManged) && isOrgOwned && isOrgAdmin && repo.membersCount === 0
    isEligibleForCopilot: false, // TODO: https://github.com/github/github/blob/392ce39489ce89031c9aeedf022eb12804d91b6e/app/components/repositories/empty_repo/copilot_or_codespace_prompt_component.rb#L100-L110
    hasCopilotAccess: false, // TODO: copilot_user&.access_allowed?
  }
}

// ---------------------------------------------------------------------------
// Primary prompt: Copilot or Codespaces
// ---------------------------------------------------------------------------

type CodespacesOrCopilotPromptVariant =
  | 'copilot-org-admin'
  | 'copilot-org-member'
  | 'copilot-individual'
  | 'codespaces'
  | null

function determineCodespacesOrCopilotVariant(e: Entitlements): CodespacesOrCopilotPromptVariant {
  if (e.showOrgAdminPrompt) return null

  if (e.isEligibleForCopilot) {
    if (e.isOrgOwned && e.isEnterpriseOrg) {
      return e.isOrgAdmin ? 'copilot-org-admin' : 'copilot-org-member'
    }
    if (!e.isOrgOwned && !e.hasCopilotAccess) return 'copilot-individual'
  }

  return 'codespaces'
}

function CodespacesOrCopilotPrompt({entitlements}: {entitlements: Entitlements}) {
  const variant = determineCodespacesOrCopilotVariant(entitlements)
  if (!variant) return null

  return (
    <Stack.Item grow className={styles.PromptItem}>
      {variant === 'copilot-org-admin' && <CopilotOrgAdminPrompt />}
      {variant === 'copilot-org-member' && <CopilotOrgMemberPrompt />}
      {variant === 'copilot-individual' && <CopilotIndividualPrompt />}
      {variant === 'codespaces' && <CodespacesPrompt />}
    </Stack.Item>
  )
}

function CopilotOrgAdminPrompt() {
  return (
    <PromptCard
      icon={CopilotIcon}
      title="Set up GitHub Copilot"
      description="Use GitHub's AI pair programmer to autocomplete suggestions as you code."
      buttonLabel="Get started with GitHub Copilot"
      // TODO: copilot_business_signup_organization_payment_path(org: repo_organization)
      href="#"
    />
  )
}

function CopilotOrgMemberPrompt() {
  return (
    <PromptCard
      icon={CopilotIcon}
      title="Take flight with GitHub Copilot"
      description="Use GitHub's AI pair programmer to autocomplete suggestions as you code."
      // TODO: this should be a Member Feature Request flow ("Ask admin for access" /
      // "Remove request" / "You've successfully submitted a request..." states).
      // The legacy ERB renders Organizations::MemberRequests::FeatureRequestComponent.
      // Replace this static button once a React equivalent is available.
      buttonLabel="Ask admin for access"
      href="#"
    />
  )
}

function CopilotIndividualPrompt() {
  return (
    <PromptCard
      icon={CopilotIcon}
      title="Set up GitHub Copilot"
      description="Use GitHub's AI pair programmer to autocomplete suggestions as you code."
      buttonLabel="Get started with GitHub Copilot"
      // TODO: user_part_of_org? ? copilot_settings_path : copilot_signup_path
      href="#"
    />
  )
}

function CodespacesPrompt() {
  return (
    <PromptCard
      icon={CodespacesIcon}
      title="Start coding with Codespaces"
      description="Add a README file and start coding in a secure, configurable, and dedicated development environment."
      buttonLabel="Create a codespace"
      // TODO: codespaces_empty_repo_init flag → new_with_nwo_codespaces_path(...),
      // otherwise repository_codespaces_path(repository).
      href="#"
    />
  )
}

// ---------------------------------------------------------------------------
// Secondary prompt: collaboration / org CTAs
// ---------------------------------------------------------------------------

type InvitePromptVariant = 'org' | 'move-to-org' | 'user-default' | 'org-admin'

function determineInvitePromptVariant(e: Entitlements): InvitePromptVariant {
  if (e.showOrgAdminPrompt) return 'org-admin'
  if (e.isOrgOwned) return 'org'

  // TODO: return 'move-to-org' when repo.membersCount >= 5.
  return 'user-default'
}

function InvitePrompt({entitlements}: {entitlements: Entitlements}) {
  const variant = determineInvitePromptVariant(entitlements)
  return (
    <Stack.Item grow className={styles.PromptItem}>
      {variant === 'org' && <ManageOrgAccessPrompt />}
      {variant === 'move-to-org' && <MoveToOrganizationPrompt />}
      {variant === 'org-admin' && <OrgAdminPrompt />}
      {variant === 'user-default' && <InviteCollaboratorsPrompt />}
    </Stack.Item>
  )
}

function ManageOrgAccessPrompt() {
  return (
    <PromptCard
      icon={PersonAddIcon}
      title="Give access to the people you work with"
      description="Ensure the right people and teams have access to this repository."
      buttonLabel="Manage access"
      // TODO: repository_access_management_path(current_repository.owner, current_repository)
      href="#"
    />
  )
}

function OrgAdminPrompt() {
  return (
    <PromptCard
      icon={PeopleIcon}
      title="Give access to the people you work with"
      description="Ensure the right people and teams have access to this repository."
      buttonLabel="Add teams and collaborators"
      // TODO: repository_access_management_path(current_repository.owner, current_repository)
      href="#"
    />
  )
}

function MoveToOrganizationPrompt() {
  return (
    <PromptCard
      icon={PeopleIcon}
      title="Move to an organization"
      description="Collaborate across many projects, with security and administrative features."
      buttonLabel="Move repository"
      // TODO: new_move_work_path(current_user, repository: current_repository)
      href="#"
    />
  )
}

function InviteCollaboratorsPrompt() {
  return (
    <PromptCard
      icon={PersonAddIcon}
      title="Add collaborators to this repository"
      description="Search for people using their GitHub username or email address."
      buttonLabel="Invite collaborators"
      // TODO: repository_access_management_path(current_repository.owner, current_repository)
      href="#"
    />
  )
}

// ---------------------------------------------------------------------------
// Shared card
// ---------------------------------------------------------------------------

interface PromptCardProps {
  icon: Icon
  title: string
  description: string
  buttonLabel: string
  href: string
}

function PromptCard({icon, title, description, buttonLabel, href}: PromptCardProps) {
  return (
    <Card className={styles.Prompt}>
      <Card.Icon icon={icon} />
      <Card.Heading>{title}</Card.Heading>
      <Card.Description>{description}</Card.Description>
      <Card.Metadata>
        <LinkButton href={href}>{buttonLabel}</LinkButton>
      </Card.Metadata>
    </Card>
  )
}
